/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * PaymentAnalyticsPage
 * --------------------
 * Revenue, funnel, coupon, refund and reconciliation analytics for one
 * application (PAYMENT_ANALYTICS_DEBUGGING.md §1–§5).
 *
 * Follows the NotificationAnalyticsPage pattern: back-button header,
 * theme-aware cards, responsive grid, ProgressBarDialog while loading.
 *
 * Two things about this API that the UI has to respect:
 *
 * 1. MONEY ARRIVES AS DECIMAL STRINGS ("6300.00"), not numbers. That is
 *    deliberate — floats lose precision on money. Format them for display,
 *    but never add them up here: any total the user needs is computed by the
 *    backend, where the arithmetic is done in Decimal. `formatMoney` parses
 *    only to place separators.
 *
 * 2. Each section filters on a DIFFERENT timestamp (revenue on
 *    completed_at, the funnel on created_at, and so on), and the default
 *    window ends at "now". The API echoes `window.anchor` for exactly this
 *    reason, and every tab shows it, so two numbers that legitimately
 *    disagree can be explained rather than looking like a bug.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import ProgressBarDialog from '../components/common/ProgressBar';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret
});

const TABS = [
    { key: 'revenue', label: 'Revenue', icon: 'bi-cash-stack', path: 'revenue/' },
    { key: 'funnel', label: 'Funnel', icon: 'bi-funnel', path: 'funnel/' },
    { key: 'coupons', label: 'Coupons', icon: 'bi-tag', path: 'coupons/' },
    { key: 'refunds', label: 'Refunds', icon: 'bi-arrow-counterclockwise', path: 'refunds/' },
    { key: 'reconciliation', label: 'Reconciliation', icon: 'bi-shield-check', path: 'reconciliation/' }
];

/**
 * §0 — revenue grouped by organizer. Fetched alongside the Revenue tab rather
 * than given a tab of its own, because the point of §0 is that the grouping is
 * always present, not an alternative view you have to go and find.
 */
const BY_ORGANIZER = { key: 'byOrganizer', path: 'revenue/by-organizer/', label: 'Revenue by organizer' };

function formatDate(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function defaultStartDate() {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return formatDate(d);
}

function defaultEndDate() {
    return formatDate(new Date());
}

/**
 * Format a decimal STRING from the API for display.
 * Never used to compute — only to place thousands separators.
 */
function formatMoney(value, currency) {
    if (value === null || value === undefined) return '—';
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    const formatted = n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    return currency ? `${currency} ${formatted}` : formatted;
}

function formatPercent(value) {
    if (value === null || value === undefined) return '—';
    return `${value}%`;
}

function formatDateTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    // new Date('garbage') does not throw — it returns an Invalid Date, which
    // toLocaleString() happily renders as the string "Invalid Date".
    return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

/** A labelled metric card with a coloured top rule. */
function MetricCard({ label, value, sub, icon, color, theme }) {
    return (
        <div className="col-12 col-sm-6 col-md-4 col-lg">
            <div
                className="card border-0 shadow-sm h-100"
                style={{ borderRadius: theme.cardRadius, borderTop: `3px solid ${color}` }}
            >
                <div className="card-body p-3 text-center">
                    <div className="mb-2">
                        <i className={`bi ${icon}`} aria-hidden="true" style={{ fontSize: '1.5rem', color }}></i>
                    </div>
                    <div className="fw-bold fs-5" style={{ color }}>{value}</div>
                    <div className="text-muted small mt-1">{label}</div>
                    {sub && <div className="text-muted mt-1" style={{ fontSize: '11px' }}>{sub}</div>}
                </div>
            </div>
        </div>
    );
}

function SectionCard({ title, icon, iconColor, subtitle, children, theme }) {
    return (
        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: theme.cardRadius }}>
            <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                <div className="d-flex align-items-center gap-2">
                    <i className={`bi ${icon}`} aria-hidden="true" style={{ fontSize: '1.2rem', color: iconColor }}></i>
                    <h6 className="fw-bold mb-0 text-dark">{title}</h6>
                </div>
                {subtitle && <div className="text-muted mt-1" style={{ fontSize: '12px' }}>{subtitle}</div>}
            </div>
            <div className="card-body px-4 pt-3">{children}</div>
        </div>
    );
}

const GRANULARITIES = [
    { key: '', label: 'Auto' },
    { key: 'day', label: 'Day' },
    { key: 'week', label: 'Week' },
    { key: 'month', label: 'Month' },
    { key: 'year', label: 'Year' }
];

/**
 * A bucket's axis label, according to how the series was bucketed.
 * The API returns the bucket's START date, so a week reads as "w/c 15 Sep"
 * and a month as "Sep 2026" rather than a bare day that means something else.
 */
function bucketLabel(day, granularity) {
    if (!day) return '';
    const text = String(day);
    // Accept either shape defensively: the API normalises buckets to plain
    // dates, but a datetime here must not fall through as a raw ISO string.
    const d = new Date(text.includes('T') ? text : `${text}T00:00:00`);
    if (Number.isNaN(d.getTime())) return text.slice(0, 10);
    switch (granularity) {
        case 'year':
            return d.toLocaleDateString(undefined, { year: 'numeric' });
        case 'month':
            return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
        case 'week':
            // The subtitle already says "per week"; "w/c" just made the label
            // wider than its column and it clipped against the scroll edge.
            return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
        default:
            return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
    }
}

/** Bar width as a clamped percentage — never NaN, never negative, never >100. */
function barPct(value, max) {
    const pct = (Number(value) || 0) / (max || 1) * 100;
    if (!Number.isFinite(pct)) return 0;
    return Math.max(0, Math.min(100, Math.round(pct)));
}

/** Totals across more than one currency are a sum of unlike things — say so. */
function MixedCurrencyNotice() {
    return (
        <div className="alert d-flex align-items-start gap-2 mb-4" role="alert"
            style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 6 }}>
            <i className="bi bi-exclamation-triangle-fill mt-1" aria-hidden="true"></i>
            <div style={{ fontSize: '13px' }}>
                This application takes payments in more than one currency. The totals below add
                those currencies together, so read them alongside the per-currency breakdown.
            </div>
        </div>
    );
}

function EmptyState({ message }) {
    return <div className="text-muted text-center py-4 small">{message}</div>;
}

/** A horizontal bar row — used wherever a breakdown needs relative weight. */
function BarRow({ label, value, pct, color, theme }) {
    return (
        <div>
            <div className="d-flex justify-content-between mb-1">
                <span className="small fw-semibold">{label}</span>
                <span className="small text-muted">{value}</span>
            </div>
            <div className="progress" style={{ height: 8, borderRadius: 4, background: '#f1f5f9' }}>
                <div
                    className="progress-bar"
                    style={{
                        width: `${pct}%`,
                        background: color || theme.primary,
                        borderRadius: 4,
                        transition: 'width 0.5s ease'
                    }}
                ></div>
            </div>
        </div>
    );
}

/**
 * What one event's GST was charged under, opened on demand.
 *
 * Normally one group: an event is held in one state, so it has one shape of
 * supply. An event whose state was corrected after some entries had sold
 * genuinely holds two, because each payment froze the answer that applied to
 * it — so both are shown rather than averaged into one misleading row.
 */
export function EventTaxDetail({ row, currency }) {
    const detail = row.tax_detail || [];
    const exempt = (Number(row.net_amount) || 0) - (Number(row.taxable_amount) || 0);

    return (
        <div
            className="mt-2 px-3 py-2"
            data-testid="event-tax-detail"
            style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderLeft: '2px solid #0f6e5c',
                borderRadius: '0 4px 4px 0',
                fontSize: 12
            }}
        >
            {detail.map((group, i) => (
                <div key={`${group.supply_type}-${group.place_of_supply}-${i}`}
                    className={i ? 'mt-2 pt-2 border-top' : ''}>
                    <div className="d-flex justify-content-between text-muted">
                        <span>Place of supply</span>
                        <span className="fw-semibold text-dark">
                            {group.place_of_supply || '—'}
                            {group.supply_type === 'intra_state' ? ' · intra-state' : ' · inter-state'}
                        </span>
                    </div>
                    <div className="d-flex justify-content-between text-muted">
                        <span>Taxable value</span>
                        <span>{formatMoney(group.taxable_amount, currency)}</span>
                    </div>
                    {(group.components || []).map(head => (
                        <div key={head.code} className="d-flex justify-content-between text-muted">
                            <span>{head.code}</span>
                            <span>{formatMoney(head.amount, currency)}</span>
                        </div>
                    ))}
                </div>
            ))}

            {exempt > 0 && (
                <div className="d-flex justify-content-between text-muted mt-2 pt-2 border-top">
                    <span>Sales below the tax threshold</span>
                    <span>{formatMoney(exempt, currency)}</span>
                </div>
            )}

            {/* The figure that actually reached the bank. Spelled out because
                revenue and tax are easy to read the wrong way round, and only
                their sum was ever charged to anyone. */}
            <div className="d-flex justify-content-between fw-semibold text-dark mt-2 pt-2 border-top">
                <span>Collected</span>
                <span>{formatMoney(row.collected_amount, currency)}</span>
            </div>
        </div>
    );
}

/** One event: its revenue bar, the GST charged ON TOP of it, and the detail. */
export function EventRevenueRow({ row, max, currency, theme, open, onToggle }) {
    const tax = Number(row.tax_amount) || 0;
    const hasDetail = (row.tax_detail || []).length > 0;
    const label = `Event ${row.chargeable_entity_parent_id ?? '—'}`;

    return (
        <div>
            <div className="d-flex justify-content-between mb-1">
                <span className="small fw-semibold">{label}</span>
                <span className="small text-muted">{formatMoney(row.net_amount, currency)}</span>
            </div>
            <div className="progress" style={{ height: 8, borderRadius: 4, background: '#f1f5f9' }}>
                <div
                    className="progress-bar"
                    style={{
                        width: `${barPct(row.net_amount, max)}%`,
                        background: theme.primary,
                        borderRadius: 4,
                        transition: 'width 0.5s ease'
                    }}
                ></div>
            </div>

            {/* Right-aligned, so the tax reads as a second line of the figure
                above it. The "+" is load-bearing: the bar is revenue BEFORE
                tax, and the tax was charged on top of it. */}
            <div className="d-flex justify-content-end mt-1" style={{ fontSize: 11 }}>
                {tax > 0 ? (
                    <button
                        type="button"
                        className="d-inline-flex align-items-center gap-1"
                        style={{
                            font: 'inherit', fontSize: 11, fontWeight: 500,
                            color: theme.primary, background: 'none',
                            border: 0, padding: 0, cursor: 'pointer',
                            textDecoration: 'none'
                        }}
                        aria-expanded={open}
                        onClick={onToggle}
                        disabled={!hasDetail}
                        title={`${formatMoney(row.net_amount, currency)} revenue + ${formatMoney(row.tax_amount, currency)} GST = ${formatMoney(row.collected_amount, currency)} collected`}
                    >
                        + {formatMoney(row.tax_amount, currency)} GST
                        {hasDetail && (
                            <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden="true"></i>
                        )}
                    </button>
                ) : (
                    // Said rather than left blank: a blank reads as broken,
                    // this reads as answered.
                    <span className="text-muted">no GST</span>
                )}
            </div>

            {open && hasDetail && <EventTaxDetail row={row} currency={currency} />}
        </div>
    );
}

/** How many event rows are shown before the panel stops growing. */
export const EVENT_ROWS_SHOWN = 5;

/**
 * Revenue by event, with a ceiling.
 *
 * The old panel rendered one row per event and so had no maximum height —
 * page length was decided by how many events had sold. This shows the top
 * few (the list arrives ranked, so that is a slice, not a sort), and expands
 * into a scrolling area rather than a longer page.
 *
 * ONE ROW OPEN AT A TIME. Opening a second closes the first, so the panel
 * cannot grow by accumulating open rows — which was the same unbounded-height
 * problem arriving by a different route.
 */
export function EventRevenuePanel({ rows, currency, theme }) {
    const [showAll, setShowAll] = useState(false);
    const [openId, setOpenId] = useState(null);

    const max = Math.max(...rows.map(r => Number(r.net_amount) || 0), 1);
    const shown = showAll ? rows : rows.slice(0, EVENT_ROWS_SHOWN);

    const totals = rows.reduce((acc, r) => ({
        net: acc.net + (Number(r.net_amount) || 0),
        tax: acc.tax + (Number(r.tax_amount) || 0),
        collected: acc.collected + (Number(r.collected_amount) || 0)
    }), { net: 0, tax: 0, collected: 0 });

    return (
        <>
            <div
                className="d-flex flex-column gap-3"
                style={showAll ? { maxHeight: 420, overflowY: 'auto' } : undefined}
            >
                {shown.map((row, i) => {
                    const id = row.chargeable_entity_parent_id ?? `#${i}`;
                    return (
                        <EventRevenueRow
                            key={id}
                            row={row}
                            max={max}
                            currency={currency}
                            theme={theme}
                            open={openId === id}
                            onToggle={() => setOpenId(openId === id ? null : id)}
                        />
                    );
                })}
            </div>

            {/* The footer states what is hidden and what was actually banked,
                so the panel never implies the top few is everything. */}
            <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2 mt-3 pt-3 border-top">
                <span className="text-muted" style={{ fontSize: 11 }}>
                    {rows.length} {rows.length === 1 ? 'event' : 'events'} · net {formatMoney(totals.net, currency)}
                    {totals.tax > 0 && <> + GST {formatMoney(totals.tax, currency)} = collected {formatMoney(totals.collected, currency)}</>}
                </span>
                {rows.length > EVENT_ROWS_SHOWN && (
                    <button
                        type="button"
                        style={{
                            font: 'inherit', fontSize: 11, fontWeight: 600,
                            color: theme.primary, background: 'none',
                            border: 0, padding: 0, cursor: 'pointer',
                            textDecoration: 'none'
                        }}
                        onClick={() => setShowAll(!showAll)}
                    >
                        {showAll ? 'Show top ' + EVENT_ROWS_SHOWN : `Show all ${rows.length}`}
                    </button>
                )}
            </div>
        </>
    );
}

/**
 * GST collected, split into the heads a return is filed under.
 *
 * Nothing is stored per head: an intra-state total is two identical halves by
 * construction and an inter-state one is IGST entire, so the server derives
 * these from the tax actually collected. They therefore always add up to it.
 */
export function TaxByHeadPanel({ d, currency, theme }) {
    const heads = d.tax_by_head || [];
    const max = Math.max(...heads.map(h => Number(h.amount) || 0), 1);

    return (
        <SectionCard
            title="GST collected, by head" icon="bi-percent" iconColor="#0f6e5c"
            subtitle="charged on top of revenue" theme={theme}
        >
            {!heads.length ? <EmptyState message="No GST collected in this range." /> : (
                <>
                    <div className="d-flex flex-column gap-3">
                        {heads.map(head => (
                            <BarRow
                                key={head.code}
                                label={head.code}
                                value={formatMoney(head.amount, currency)}
                                pct={barPct(head.amount, max)}
                                color="#0f6e5c"
                                theme={theme}
                            />
                        ))}
                    </div>
                    <div className="d-flex justify-content-between flex-wrap gap-2 mt-3 pt-3 border-top text-muted"
                        style={{ fontSize: 11 }}>
                        <span>Taxable value {formatMoney(d.taxable_amount, currency)}</span>
                        <span>Untaxed sales {formatMoney(d.exempt_amount, currency)}</span>
                    </div>
                </>
            )}
        </SectionCard>
    );
}

/**
 * The same money grouped by the state the supply was made in.
 *
 * This is the filing view: IGST is apportioned to the destination state, so a
 * state-wise taxable total is what a monthly return asks for. It costs
 * nothing extra — the place of supply is already frozen on every payment.
 */
export function TaxByPlacePanel({ d, currency, theme }) {
    const rows = d.tax_by_place_of_supply || [];
    const total = rows.reduce((sum, r) => sum + (Number(r.tax_amount) || 0), 0);

    return (
        <SectionCard
            title="GST by place of supply" icon="bi-geo-alt" iconColor="#7c3aed"
            subtitle="the state each event was held in" theme={theme}
        >
            {!rows.length ? <EmptyState message="No GST collected in this range." /> : (
                <>
                    <div className="table-responsive">
                        <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                            <thead>
                                <tr className="text-muted" style={{ fontSize: 11 }}>
                                    <th className="fw-normal">State</th>
                                    <th className="fw-normal text-end">Taxable</th>
                                    <th className="fw-normal text-end">GST</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr key={`${r.place_of_supply}-${r.supply_type}-${i}`}>
                                        <td>
                                            {r.place_of_supply || '—'}
                                            <span className="badge ms-2"
                                                style={{
                                                    background: r.supply_type === 'intra_state' ? '#e6f2ef' : '#f1f5f9',
                                                    color: r.supply_type === 'intra_state' ? '#0f6e5c' : '#64748b',
                                                    fontWeight: 500, fontSize: 10
                                                }}>
                                                {r.supply_type === 'intra_state' ? 'CGST+SGST' : 'IGST'}
                                            </span>
                                        </td>
                                        <td className="text-end">{formatMoney(r.taxable_amount, currency)}</td>
                                        <td className="text-end">{formatMoney(r.tax_amount, currency)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="d-flex justify-content-between mt-3 pt-3 border-top text-muted"
                        style={{ fontSize: 11 }}>
                        <span>{rows.length} {rows.length === 1 ? 'state' : 'states'}</span>
                        <span>{formatMoney(total, currency)}</span>
                    </div>
                </>
            )}
        </SectionCard>
    );
}

export default function PaymentAnalyticsPage() {
    const { theme } = useTheme();
    const { id: applicationId } = useParams();
    const navigate = useNavigate();
    const { showAlertDialog } = useAlertDialog();

    const [activeTab, setActiveTab] = useState('revenue');
    const [loading, setLoading] = useState({});
    const [data, setData] = useState({});
    const [errors, setErrors] = useState({});
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const [currency, setCurrency] = useState({ code: '', mixed: false });

    // The filters the DISPLAYED figures were fetched with, as opposed to what the
    // inputs currently show. Every tab is fetched with this same committed set, so
    // no two tabs can be showing different windows — which matters on a page whose
    // purpose is explaining why two numbers legitimately differ.
    const [organizer, setOrganizer] = useState('');
    const [granularity, setGranularity] = useState('');   // '' = let the window decide
    const [applied, setApplied] = useState(() => ({ from: defaultStartDate(), to: defaultEndDate(), organizer: '', granularity: '' }));

    const current = data[activeTab];
    const isLoading = !!loading[activeTab];
    const invalidRange = startDate > endDate;
    const rangeDirty = startDate !== applied.from || endDate !== applied.to || organizer !== applied.organizer;

    // Monotonic request id per tab. A response only writes state if it is still
    // the newest request for its tab; otherwise a slow earlier request can land
    // after a newer one and put pre-refresh figures on a refreshed page.
    const reqIds = useRef({});

    // Which tabs have a request in the air. Both fetch effects depend on `data`,
    // so a response landing re-runs them while another request is still
    // outstanding and its cache slot still empty; without this they would fire
    // again. Together with the cache check, this makes "fetch once" true.
    const inFlight = useRef({});

    const fetchTab = useCallback(async (tabKey, range) => {
        const target = tabKey === BY_ORGANIZER.key ? BY_ORGANIZER : TABS.find(t => t.key === tabKey);
        if (!target) return;
        const { from, to } = range;
        if (from > to) return;  // the inline warning already says why

        const myId = (reqIds.current[tabKey] || 0) + 1;
        reqIds.current[tabKey] = myId;
        inFlight.current[tabKey] = true;
        const isCurrent = () => reqIds.current[tabKey] === myId;

        setLoading(prev => ({ ...prev, [tabKey]: true }));
        try {
            const params = new URLSearchParams({
                application_id: String(applicationId),
                start_date: from,
                end_date: to
            });
            // Only the revenue endpoint takes an organizer filter — a Payment,
            // a Refund and a webhook event are not organizer-scoped.
            if (tabKey === 'revenue' && range.organizer) {
                params.set('organizer_instance_id', range.organizer);
            }
            if (tabKey === 'revenue' && range.granularity) {
                params.set('granularity', range.granularity);
            }
            const resp = await apiHelper.apiCall(
                `/arcee/payments/analytics/${target.path}?${params.toString()}`,
                { method: 'GET' }
            );
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.error || `Failed to load ${target.label} (${resp.status})`);
            }
            const body = await resp.json();
            if (!isCurrent()) return;
            setData(prev => ({ ...prev, [tabKey]: body }));
            setErrors(prev => ({ ...prev, [tabKey]: null }));

            // Currency is established from the revenue response and then kept, so
            // it survives a Refresh taken while sitting on another tab.
            if (tabKey === 'revenue' && Array.isArray(body.by_currency)) {
                if (body.by_currency.length === 1) {
                    setCurrency({ code: body.by_currency[0].currency_code || '', mixed: false });
                } else if (body.by_currency.length > 1) {
                    setCurrency({ code: '', mixed: true });
                }
            }
        } catch (error) {
            if (!isCurrent()) return;
            const message = error.message || 'Failed to load payment analytics';
            // Recorded on the page as well as raised in a dialog, so that a
            // dismissed dialog still leaves the reason on screen. "No data" and
            // "you cannot see this application" must not look alike.
            setErrors(prev => ({ ...prev, [tabKey]: message }));
            showAlertDialog({ title: 'Error', message, type: 'error' });
        } finally {
            if (isCurrent()) {
                inFlight.current[tabKey] = false;
                setLoading(prev => ({ ...prev, [tabKey]: false }));
            }
        }
    }, [applicationId, showAlertDialog]);

    // Switching application must not leave another application's figures behind.
    // React Router keeps this component mounted when only :id changes, so without
    // this, tabs loaded for app A stay readable under app B's header.
    useEffect(() => {
        setData({});
        setErrors({});
        setCurrency({ code: '', mixed: false });
        setOrganizer('');
        setGranularity('');
        setApplied(prev => ({ ...prev, organizer: '', granularity: '' }));
        reqIds.current = {};
        inFlight.current = {};
    }, [applicationId]);

    // Fetch a tab only when it has nothing cached. Refresh is the explicit
    // refetch, and it clears the cache to force one.
    useEffect(() => {
        if (data[activeTab] || errors[activeTab] || inFlight.current[activeTab]) return;
        fetchTab(activeTab, applied);
    }, [activeTab, applied, data, errors, fetchTab]);

    // Keep the organizer breakdown in step with an applied-filter change.
    useEffect(() => {
        if (activeTab !== 'revenue') return;
        if (inFlight.current[BY_ORGANIZER.key]) return;
        if (data[BY_ORGANIZER.key] || errors[BY_ORGANIZER.key]) return;
        fetchTab(BY_ORGANIZER.key, applied);
    }, [activeTab, applied, data, errors, fetchTab]);

    const handleRefresh = () => {
        if (invalidRange) return;
        setData({});
        setErrors({});
        reqIds.current = {};
        inFlight.current = {};
        setApplied({ from: startDate, to: endDate, organizer, granularity });
    };

    // Options come from the grouped response itself, so the day a real organizer
    // appears it shows up here with no code change — the §0 promise, in the UI.
    const organizerOptions = (data[BY_ORGANIZER.key]?.organizers || [])
        .filter(o => o.organizer_instance_id != null)
        .map(o => ({ value: String(o.organizer_instance_id), label: `Organizer ${o.organizer_instance_id}` }));

    // Retries only the failed tab — Refresh would discard every other tab's data.
    const changeGranularity = (value) => {
        setGranularity(value);
        // Re-buckets the same window rather than changing what is counted, so
        // it applies at once instead of waiting for Refresh like the dates do.
        //
        // The existing chart stays on screen while the new one loads. Clearing
        // it first swapped the page for a spinner, the page collapsed to a
        // fraction of its height, and the browser threw the reader back to the
        // top — on a control that sits well below the fold.
        inFlight.current.revenue = false;
        setApplied(prev => ({ ...prev, granularity: value }));
        fetchTab('revenue', { ...applied, granularity: value });
    };

    const handleRetry = () => {
        inFlight.current[activeTab] = false;
        setErrors(prev => ({ ...prev, [activeTab]: null }));
        fetchTab(activeTab, applied);
    };

    return (
        <div className="d-flex flex-column w-100" style={{ background: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' }}>

            {/* Header */}
            <div className="w-100 px-3 py-3 px-md-4 py-md-4 bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3">
                    <button
                        className="btn btn-light rounded-circle shadow-sm"
                        aria-label="Back to application"
                        onClick={() => navigate(`/dashboard/applications/${applicationId}`)}
                    >
                        <i className="bi bi-arrow-left"></i>
                    </button>
                    <div>
                        <h4 className="mb-0 fw-bold text-dark">Payment Analytics</h4>
                        <small className="text-muted d-none d-md-block">
                            Revenue, conversion and reconciliation for this application.
                        </small>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="w-100 bg-white border-bottom px-3 px-md-4">
                <ul className="nav nav-tabs border-0" role="tablist" style={{ marginBottom: '-1px' }}>
                    {TABS.map(t => (
                        <li className="nav-item" key={t.key}>
                            <button
                                className="nav-link border-0 bg-transparent d-flex align-items-center gap-2"
                                role="tab"
                                aria-selected={activeTab === t.key}
                                onClick={() => setActiveTab(t.key)}
                                style={{
                                    fontSize: '13.5px',
                                    fontWeight: 600,
                                    color: activeTab === t.key ? theme.primary : (theme.primaryLight || '#5b6675'),
                                    borderBottom: activeTab === t.key
                                        ? `3px solid ${theme.primary}`
                                        : '3px solid transparent',
                                    borderRadius: 0
                                }}
                            >
                                <i className={`bi ${t.icon}`} aria-hidden="true"></i>
                                {t.label}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="container-fluid p-3 p-md-4" style={{ maxWidth: 1400 }}>

                {/* Date range */}
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: theme.cardRadius }}>
                    <div className="card-body py-3 px-4">
                        <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3 flex-wrap">
                            <div className="d-flex align-items-center gap-2">
                                <i className="bi bi-calendar3 text-muted"></i>
                                <label htmlFor="pa-from" className="small fw-bold text-muted mb-0">From</label>
                                <input
                                    id="pa-from"
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={startDate}
                                    max={defaultEndDate()}
                                    onChange={e => setStartDate(e.target.value)}
                                    style={{ maxWidth: 160, borderRadius: theme.inputRadius || '6px' }}
                                />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <label htmlFor="pa-to" className="small fw-bold text-muted mb-0">To</label>
                                <input
                                    id="pa-to"
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={endDate}
                                    max={defaultEndDate()}
                                    onChange={e => setEndDate(e.target.value)}
                                    style={{ maxWidth: 160, borderRadius: theme.inputRadius || '6px' }}
                                />
                            </div>
                            {activeTab === 'revenue' && (
                                <div className="d-flex align-items-center gap-2">
                                    <label htmlFor="pa-organizer" className="small fw-bold text-muted mb-0">Organizer</label>
                                    <select
                                        id="pa-organizer"
                                        className="form-select form-select-sm"
                                        value={organizer}
                                        onChange={e => setOrganizer(e.target.value)}
                                        style={{ maxWidth: 210, borderRadius: theme.inputRadius || '6px' }}
                                    >
                                        <option value="">All organizers (grouped)</option>
                                        {organizerOptions.map(o => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <button
                                className="btn btn-sm d-flex align-items-center gap-2"
                                onClick={handleRefresh}
                                disabled={isLoading || invalidRange}
                                style={{ background: theme.primary, color: '#fff', border: 'none', borderRadius: theme.buttonRadius }}
                            >
                                <i className="bi bi-arrow-clockwise"></i>
                                Refresh
                            </button>
                            {invalidRange ? (
                                <span className="text-danger small">
                                    <i className="bi bi-exclamation-circle me-1" aria-hidden="true"></i>
                                    Start date cannot be after end date
                                </span>
                            ) : rangeDirty ? (
                                <span className="small" style={{ color: '#b45309' }}>
                                    <i className="bi bi-info-circle me-1" aria-hidden="true"></i>
                                    Press Refresh to apply this range
                                </span>
                            ) : null}
                            {isLoading && current && (
                                <span className="small d-flex align-items-center gap-1" style={{ color: theme.primary }}>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"
                                        style={{ width: 12, height: 12, borderWidth: 2 }}></span>
                                    Updating…
                                </span>
                            )}
                            {current?.window && (
                                <span className="text-muted ms-auto" style={{ fontSize: '11.5px' }}>
                                    {applied.from} → {applied.to} · filtered on <code>{current.window.anchor}</code>
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* The full-page loader is only for a tab with nothing to show yet.
                    Once a tab has content, a refetch (changing bucket size, or
                    Refresh) leaves it on screen and swaps the data underneath —
                    swapping it for a spinner made the page jump. */}
                {isLoading && !current ? (
                    <ProgressBarDialog open={true} text_message="Loading payment analytics..." />
                ) : errors[activeTab] ? (
                    <div className="card border-0 shadow-sm" style={{ borderRadius: theme.cardRadius }}>
                        <div className="card-body text-center py-5">
                            <i className="bi bi-exclamation-octagon d-block mb-3"
                                style={{ fontSize: '2rem', color: '#dc2626' }}></i>
                            <div className="fw-semibold text-dark mb-1">This section could not be loaded</div>
                            <div className="text-muted small mb-3">{errors[activeTab]}</div>
                            <button className="btn btn-sm"
                                onClick={handleRetry}
                                style={{ background: theme.primary, color: '#fff', border: 'none', borderRadius: theme.buttonRadius }}>
                                <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>Try again
                            </button>
                        </div>
                    </div>
                ) : !current ? (
                    <div className="card border-0 shadow-sm" style={{ borderRadius: theme.cardRadius }}>
                        <div className="card-body"><EmptyState message="No data loaded." /></div>
                    </div>
                ) : (
                    <>
                        {currency.mixed && <MixedCurrencyNotice />}
                        {activeTab === 'revenue' && <RevenueTab d={current} theme={theme} currency={currency.code} granularity={granularity} onGranularity={changeGranularity} byOrganizer={data[BY_ORGANIZER.key]} organizerError={errors[BY_ORGANIZER.key]} />}
                        {activeTab === 'funnel' && <FunnelTab d={current} theme={theme} currency={currency.code} />}
                        {activeTab === 'coupons' && <CouponsTab d={current} theme={theme} currency={currency.code} />}
                        {activeTab === 'refunds' && <RefundsTab d={current} theme={theme} currency={currency.code} />}
                        {activeTab === 'reconciliation' && <ReconciliationTab d={current} theme={theme} currency={currency.code} />}
                    </>
                )}
            </div>
        </div>
    );
}

/* ── §1 Revenue ────────────────────────────────────────── */
function RevenueTab({ d, theme, currency, byOrganizer, organizerError, granularity, onGranularity }) {
    const maxEntity = Math.max(...(d.by_entity_type || []).map(r => Number(r.net_amount) || 0), 1);
    const maxDaily = Math.max(...(d.daily || []).map(r => Number(r.net_amount) || 0), 1);

    return (
        <>
            <div className="row g-3 mb-4">
                <MetricCard label="Gross revenue" value={formatMoney(d.gross_amount, currency)}
                    sub="Payment.gross_amount" icon="bi-cash-stack" color={theme.primary} theme={theme} />
                <MetricCard label="Discounts" value={formatMoney(d.discount_amount, currency)}
                    sub="Payment.discount_amount" icon="bi-tags" color="#d97706" theme={theme} />
                {/* Already tax-exclusive. The old caption said Payment.amount,
                    which is the tax-INCLUSIVE column — anyone reconciling
                    against the database followed it to the wrong field. */}
                <MetricCard label="Net revenue" value={formatMoney(d.net_amount, currency)}
                    sub="excludes GST" icon="bi-graph-up-arrow" color="#16a34a" theme={theme} />
                <MetricCard label="GST collected" value={formatMoney(d.tax_collected, currency)}
                    sub="charged on top of revenue" icon="bi-percent" color="#0f6e5c" theme={theme} />
                <MetricCard label="Paid orders" value={d.paid_order_count}
                    sub="status = paid" icon="bi-bag-check" color="#0891b2" theme={theme} />
                <MetricCard label="Average order value" value={formatMoney(d.average_order_value, currency)}
                    sub="net ÷ paid orders" icon="bi-receipt" color="#7c3aed" theme={theme} />
            </div>

            {/* Revenue and tax are separate figures above, so nothing on that
                row is the money that actually arrived. Stated once here rather
                than added as a seventh card. */}
            {Number(d.tax_collected) > 0 && (
                <div className="text-muted mb-4" style={{ fontSize: 12 }}>
                    <i className="bi bi-info-circle me-1" aria-hidden="true"></i>
                    GST is charged <strong>on top of</strong> revenue, not taken out of it —
                    {' '}{formatMoney(d.net_amount, currency)} + {formatMoney(d.tax_collected, currency)}
                    {' '}= <strong>{formatMoney(d.collected_amount, currency)}</strong> collected.
                </div>
            )}

            <OrganizerBreakdown data={byOrganizer} error={organizerError} currency={currency} theme={theme} />

            <div className="row g-4">
                <div className="col-12 col-lg-6">
                    <SectionCard title="Revenue by event" icon="bi-calendar-event" iconColor={theme.primary}
                        subtitle="net revenue, with GST charged on top" theme={theme}>
                        {!(d.by_event || []).length ? <EmptyState message="No revenue in this range." /> : (
                            <EventRevenuePanel rows={d.by_event} currency={currency} theme={theme} />
                        )}
                    </SectionCard>
                </div>

                <div className="col-12 col-lg-6">
                    <SectionCard title="By chargeable entity" icon="bi-box-seam" iconColor="#7c3aed"
                        subtitle="PaymentLineItem.chargeable_entity" theme={theme}>
                        {!(d.by_entity_type || []).length ? <EmptyState message="No line items in this range." /> : (
                            <div className="d-flex flex-column gap-3">
                                {d.by_entity_type.map((r, i) => (
                                    <BarRow key={r.chargeable_entity__code ?? i}
                                        label={(r.chargeable_entity__code || '—').replace(/_/g, ' ')}
                                        value={formatMoney(r.net_amount, currency)}
                                        pct={barPct(r.net_amount, maxEntity)}
                                        color="#7c3aed"
                                        theme={theme} />
                                ))}
                            </div>
                        )}
                    </SectionCard>
                </div>

                {/* Directly beneath Revenue by event: the per-event figures
                    raise the question these two answer, so they read in that
                    order rather than before it. */}
                <div className="col-12 col-lg-6"><TaxByHeadPanel d={d} currency={currency} theme={theme} /></div>
                <div className="col-12 col-lg-6"><TaxByPlacePanel d={d} currency={currency} theme={theme} /></div>

                <div className="col-12 col-lg-8">
                    <SectionCard title="Sales over time" icon="bi-bar-chart-line" iconColor="#0891b2"
                        subtitle={`Net revenue per ${d.granularity || 'day'}, bucketed by completion${granularity ? '' : ' — bucket size chosen from the range'}`}
                        theme={theme}>
                        <div className="d-flex justify-content-end mb-3">
                            <div className="btn-group btn-group-sm" role="group" aria-label="Chart bucket size">
                                {GRANULARITIES.map(g => {
                                    const active = granularity === g.key;
                                    return (
                                        <button key={g.key || 'auto'} type="button" className="btn btn-sm"
                                            aria-pressed={active}
                                            onClick={() => onGranularity(g.key)}
                                            style={{
                                                background: active ? theme.primary : 'transparent',
                                                color: active ? '#fff' : (theme.primaryLight || '#5b6675'),
                                                border: `1px solid ${active ? theme.primary : '#cbd5e1'}`,
                                                fontWeight: 600, fontSize: '12px'
                                            }}>
                                            {g.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        {!(d.daily || []).length ? <EmptyState message="No completed payments in this range." /> : (
                            // Scrolls horizontally on its own. A year bucketed daily is 365
                            // columns; without this the labels smear into each other and the
                            // page itself would scroll sideways.
                            <div style={{ overflowX: 'auto', overflowY: 'hidden', paddingBottom: 6, maxWidth: '100%' }}>
                                <div className="d-flex align-items-end gap-2"
                                    style={{
                                        height: 200,
                                        paddingInline: 14,   // so the end labels can overhang without clipping
                                        minWidth: Math.max(d.daily.length * 58 + 28, 260)
                                    }}>
                                    {d.daily.map((r, i) => {
                                        const h = Math.max(Math.round(((Number(r.net_amount) || 0) / maxDaily) * 140), 4);
                                        const step = d.daily.length > 12 ? Math.ceil(d.daily.length / 9) : 1;
                                        const showLabel = i % step === 0 || i === d.daily.length - 1;
                                        const label = bucketLabel(r.day, d.granularity);
                                        return (
                                            <div key={r.day ?? i} className="d-flex flex-column align-items-center"
                                                style={{ width: 54, flex: '0 0 auto' }}>
                                                <div className="text-muted mb-1"
                                                    style={{ fontSize: '10px', whiteSpace: 'nowrap', minHeight: 14 }}>
                                                    {d.daily.length <= 12 ? formatMoney(r.net_amount) : ''}
                                                </div>
                                                <div title={`${label}: ${formatMoney(r.net_amount, currency)} (${r.order_count} orders)`}
                                                    style={{ width: 42, height: h, background: theme.primary, borderRadius: '4px 4px 0 0' }}>
                                                </div>
                                                <div className="text-muted mt-1"
                                                    style={{ fontSize: '10px', whiteSpace: 'nowrap', minHeight: 14 }}>
                                                    {showLabel ? label : ''}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {(d.daily || []).length > 12 && (
                            <div className="text-muted mt-2" style={{ fontSize: '11.5px' }}>
                                <i className="bi bi-arrow-left-right me-1" aria-hidden="true"></i>
                                {d.daily.length} buckets — scroll sideways, or pick a coarser size above.
                            </div>
                        )}
                    </SectionCard>
                </div>

                <div className="col-12 col-lg-4">
                    <SectionCard title="Currency" icon="bi-currency-exchange" iconColor="#16a34a"
                        subtitle="Payment.currency" theme={theme}>
                        {!(d.by_currency || []).length ? <EmptyState message="No data available" /> : (
                            <table className="table table-sm table-borderless mb-0">
                                <thead>
                                    <tr style={{ fontSize: '12px', color: theme.primary }}>
                                        <th className="fw-semibold">Currency</th>
                                        <th className="fw-semibold text-end">Orders</th>
                                        <th className="fw-semibold text-end">Net</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {d.by_currency.map((r, i) => (
                                        <tr key={r.currency_code ?? i} style={{ fontSize: '13px' }}>
                                            <td className="fw-semibold">{r.currency_code}</td>
                                            <td className="text-end text-muted">{r.order_count}</td>
                                            <td className="text-end fw-semibold">{formatMoney(r.net_amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </SectionCard>
                </div>
            </div>
        </>
    );
}

/**
 * §0 — revenue grouped by organizer_instance_id, never filtered to NULL.
 *
 * Today this renders exactly one row, "Self-Organized", at 100%. That is the
 * design working, not a placeholder: the query groups, so the day an
 * organizer-run event exists a second row appears here on its own. Showing the
 * single bucket explicitly is what makes that visible — a card that only
 * appeared once there were two organizers would hide the invariant.
 */
function OrganizerBreakdown({ data, error, currency, theme }) {
    const rows = data?.organizers || [];
    const selfOnly = rows.length === 1 && rows[0].is_self_organized;

    return (
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: theme.cardRadius }}>
            <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-people" aria-hidden="true"
                            style={{ fontSize: '1.2rem', color: theme.primary }}></i>
                        <h6 className="fw-bold mb-0 text-dark">Revenue by organizer</h6>
                    </div>
                    <span className="badge" style={{
                        background: '#e8eaf6', color: theme.primary,
                        fontWeight: 700, fontSize: '11px', letterSpacing: '0.3px'
                    }}>
                        GROUP BY organizer_instance_id
                    </span>
                </div>
                <div className="text-muted mt-1" style={{ fontSize: '12px' }}>
                    Grouped, never filtered — the day an organizer-run event exists, a second row appears here.
                </div>
            </div>
            <div className="card-body px-4 pt-3">
                {error ? (
                    <EmptyState message={error} />
                ) : !rows.length ? (
                    <EmptyState message="No paid line items in this range." />
                ) : (
                    <>
                        <div className="d-flex mb-3" style={{ height: 26, borderRadius: 5, overflow: 'hidden' }}>
                            {rows.map((o, i) => (
                                <div key={o.organizer_instance_id ?? 'self'}
                                    title={`${o.is_self_organized ? 'Self-Organized' : 'Organizer ' + o.organizer_instance_id}: ${o.share_of_gmv}%`}
                                    style={{
                                        width: `${barPct(o.share_of_gmv, 100)}%`,
                                        background: o.is_self_organized ? theme.primary : ['#7c3aed', '#0891b2', '#d97706'][i % 3],
                                        display: 'flex', alignItems: 'center', paddingLeft: 12,
                                        color: '#fff', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap'
                                    }}>
                                    {Number(o.share_of_gmv) >= 15
                                        ? `${o.is_self_organized ? 'Self-Organized' : 'Organizer ' + o.organizer_instance_id} · ${o.share_of_gmv}%`
                                        : ''}
                                </div>
                            ))}
                        </div>
                        <div className="table-responsive">
                            <table className="table table-sm align-middle mb-0">
                                <thead>
                                    <tr style={{ fontSize: '12px', color: theme.primary }}>
                                        <th className="fw-semibold">Bucket</th>
                                        <th className="fw-semibold text-end">Net revenue</th>
                                        <th className="fw-semibold text-end">Share of GMV</th>
                                        <th className="fw-semibold text-end">Platform fee</th>
                                        <th className="fw-semibold text-end">Line items</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(o => (
                                        <tr key={o.organizer_instance_id ?? 'self'} style={{ fontSize: '13px' }}>
                                            <td>
                                                <strong>{o.is_self_organized ? 'Self-Organized' : `Organizer ${o.organizer_instance_id}`}</strong>
                                                {o.is_self_organized && (
                                                    <code className="text-muted ms-2" style={{ fontSize: '11.5px' }}>
                                                        organizer_instance_id IS NULL
                                                    </code>
                                                )}
                                            </td>
                                            <td className="text-end fw-semibold">{formatMoney(o.net_amount, currency)}</td>
                                            <td className="text-end">{formatPercent(o.share_of_gmv)}</td>
                                            <td className="text-end text-muted">{formatMoney(o.platform_fee_amount, currency)}</td>
                                            <td className="text-end text-muted">{o.line_item_count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {selfOnly && (
                            <div className="mt-3 px-3 py-2"
                                style={{ background: '#f8fafc', borderLeft: `3px solid ${theme.secondary || theme.primary}`, borderRadius: 4, fontSize: '12px' }}>
                                Every event in this application is self-organized, so the application is seller of
                                record for the whole sale. No <code>Payout</code> rows exist and no payout view is
                                rendered — there is no third party to pay.
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

/* ── §2 Funnel ─────────────────────────────────────────── */
function FunnelTab({ d, theme, currency }) {
    const total = d.total_payments || 0;
    const dist = d.status_distribution || [];
    const maxFail = Math.max(...(d.failure_reasons || []).map(r => r.count), 1);

    return (
        <>
            <div className="row g-3 mb-4">
                <MetricCard label="Checkout attempts" value={total}
                    sub="created in range" icon="bi-people" color={theme.primary} theme={theme} />
                <MetricCard label="Paid" value={d.paid_count}
                    sub="status = paid" icon="bi-check-circle" color="#16a34a" theme={theme} />
                <MetricCard label="Completion rate" value={formatPercent(d.completion_rate)}
                    sub="paid ÷ all attempts" icon="bi-percent" color="#0891b2" theme={theme} />
                <MetricCard label="Average order value" value={formatMoney(d.average_order_value, currency)}
                    sub="paid orders only" icon="bi-receipt" color="#7c3aed" theme={theme} />
                <MetricCard label="Items per checkout" value={d.items_per_checkout}
                    sub="line items ÷ paid orders" icon="bi-basket" color="#d97706" theme={theme} />
            </div>

            <div className="row g-4">
                <div className="col-12 col-lg-7">
                    <SectionCard title="Status distribution" icon="bi-funnel" iconColor={theme.primary}
                        subtitle="Every Payment.status bucket in range — the completion-rate denominator" theme={theme}>
                        {!dist.length ? <EmptyState message="No checkout attempts in this range." /> : (
                            <div className="d-flex flex-column gap-3">
                                {dist.map((r, i) => (
                                    <BarRow key={r.status ?? i}
                                        label={(r.status || '').replace(/_/g, ' ')}
                                        value={`${r.count} (${total ? Math.round((r.count / total) * 100) : 0}%)`}
                                        pct={barPct(r.count, total)}
                                        color={r.status === 'paid' ? '#16a34a' : r.status === 'cancelled' ? '#dc2626' : theme.primary}
                                        theme={theme} />
                                ))}
                            </div>
                        )}
                    </SectionCard>
                </div>

                <div className="col-12 col-lg-5">
                    <SectionCard title="Payment failures" icon="bi-exclamation-triangle" iconColor="#dc2626"
                        subtitle="Payment.failure_reason, grouped" theme={theme}>
                        {!(d.failure_reasons || []).length ? <EmptyState message="No recorded failure reasons." /> : (
                            <div className="d-flex flex-column gap-3">
                                {d.failure_reasons.map((r, i) => (
                                    <BarRow key={r.failure_reason || i} label={r.failure_reason || 'Unspecified'} value={r.count}
                                        pct={barPct(r.count, maxFail)} color="#dc2626" theme={theme} />
                                ))}
                            </div>
                        )}
                    </SectionCard>
                </div>
            </div>
        </>
    );
}

/* ── §3 Coupons ────────────────────────────────────────── */
function CouponsTab({ d, theme, currency }) {
    const coupons = d.coupons || [];
    return (
        <SectionCard title="Coupon performance" icon="bi-tag" iconColor={theme.primary}
            subtitle="CouponCode.redemption_count cross-checked against actual CouponRedemption rows" theme={theme}>
            {!coupons.length ? <EmptyState message="No coupon codes configured for this application." /> : (
                <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                        <thead>
                            <tr style={{ fontSize: '12px', color: theme.primary }}>
                                <th className="fw-semibold">Code</th>
                                <th className="fw-semibold">Discount</th>
                                <th className="fw-semibold text-end">Redemptions</th>
                                <th className="fw-semibold text-end">Given away</th>
                                <th className="fw-semibold text-end">Attributed order value</th>
                                <th className="fw-semibold text-end">Remaining</th>
                            </tr>
                        </thead>
                        <tbody>
                            {coupons.map(c => (
                                <tr key={c.coupon_code_id} style={{ fontSize: '13px' }}>
                                    <td>
                                        <code className="fw-bold">{c.code}</code>
                                        {c.counter_mismatch && (
                                            <span className="badge ms-2"
                                                title={`Counter column says ${c.redemption_count_column}, actual redemption rows: ${c.redemption_rows_total}`}
                                                style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', fontWeight: 600 }}>
                                                <i className="bi bi-exclamation-triangle me-1"></i>count mismatch
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-muted">{c.discount_name}</td>
                                    <td className="text-end">{c.redemptions_in_range}</td>
                                    <td className="text-end">{formatMoney(c.discount_given, currency)}</td>
                                    <td className="text-end fw-semibold">{formatMoney(c.attributed_order_value, currency)}</td>
                                    <td className="text-end">
                                        {c.remaining_capacity == null
                                            ? <span className="text-muted">unlimited</span>
                                            : <span className={c.remaining_capacity < 0 ? 'text-danger fw-bold' : ''}>{c.remaining_capacity}</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="text-muted mt-3" style={{ fontSize: '11.5px' }}>
                        <i className="bi bi-info-circle me-1"></i>
                        Attributed order value is the whole order each code was used on, so it is not additive across rows —
                        one payment carrying two codes counts in full against both.
                    </div>
                </div>
            )}
        </SectionCard>
    );
}

/* ── §4 Refunds ────────────────────────────────────────── */
function RefundsTab({ d, theme, currency }) {
    const maxReason = Math.max(...(d.reasons || []).map(r => r.count), 1);
    const overRefunded = d.refund_rate != null && Number(d.refund_rate) > 100;

    return (
        <>
            <div className="row g-3 mb-4">
                <MetricCard label="Refunded" value={formatMoney(d.refunded_amount, currency)}
                    sub="status = refunded only" icon="bi-arrow-counterclockwise" color="#dc2626" theme={theme} />
                <MetricCard label="Refund count" value={d.refunded_count}
                    sub="completed refunds" icon="bi-receipt-cutoff" color="#d97706" theme={theme} />
                <MetricCard label="Revenue in range" value={formatMoney(d.paid_revenue_in_range, currency)}
                    sub="the denominator" icon="bi-cash" color={theme.primary} theme={theme} />
                <MetricCard label="Refund rate" value={formatPercent(d.refund_rate)}
                    sub={d.refund_rate == null ? 'no revenue to divide by' : 'refunded ÷ revenue'}
                    icon="bi-percent" color={overRefunded ? '#dc2626' : '#0891b2'} theme={theme} />
                <MetricCard label="Time to refund" value={d.average_hours_to_refund == null ? '—' : `${d.average_hours_to_refund} h`}
                    sub="processed − requested" icon="bi-clock-history" color="#7c3aed" theme={theme} />
            </div>

            {overRefunded && (
                <div className="alert d-flex align-items-start gap-2 mb-4" role="alert"
                    style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: theme.cardRadius }}>
                    <i className="bi bi-exclamation-octagon-fill mt-1"></i>
                    <div style={{ fontSize: '13px' }}>
                        <strong>Refunds exceed revenue for this period ({formatPercent(d.refund_rate)}).</strong>{' '}
                        This is arithmetically correct, not a display bug — refunds are dated by request time and revenue by
                        completion time, so refunds against older payments land here. It can also mean a payment has been
                        refunded more than once. Worth checking against the payment records.
                    </div>
                </div>
            )}

            <div className="row g-4">
                <div className="col-12 col-lg-6">
                    <SectionCard title="Refund reasons" icon="bi-chat-left-text" iconColor="#dc2626"
                        subtitle="Refund.reason, completed refunds only" theme={theme}>
                        {!(d.reasons || []).length ? <EmptyState message="No reasons recorded." /> : (
                            <div className="d-flex flex-column gap-3">
                                {d.reasons.map((r, i) => (
                                    <BarRow key={r.reason || i} label={r.reason || 'Unspecified'} value={r.count}
                                        pct={barPct(r.count, maxReason)} color="#dc2626" theme={theme} />
                                ))}
                            </div>
                        )}
                    </SectionCard>
                </div>

                <div className="col-12 col-lg-6">
                    <SectionCard title="By status" icon="bi-list-check" iconColor={theme.primary}
                        subtitle="Every Refund.status — only 'refunded' counts as money out" theme={theme}>
                        {!(d.status_breakdown || []).length ? <EmptyState message="No refunds in this range." /> : (
                            <table className="table table-sm table-borderless mb-0">
                                <tbody>
                                    {d.status_breakdown.map((r, i) => (
                                        <tr key={r.status ?? i} style={{ fontSize: '13px' }}>
                                            <td className="text-capitalize">{(r.status || '').replace(/_/g, ' ')}</td>
                                            <td className="text-end fw-semibold">{r.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </SectionCard>
                </div>
            </div>
        </>
    );
}

/* ── §5 Reconciliation ─────────────────────────────────── */
function ReconciliationTab({ d, theme, currency }) {
    const breakdown = d.webhook_breakdown || [];
    const total = d.webhook_total || 0;
    const statusColor = s => ({
        processed: '#16a34a',
        signature_invalid: '#d97706',
        failed: '#dc2626',
        ignored: '#94a3b8',
        received: '#0891b2'
    }[s] || theme.primary);
    const statusIcon = s => ({
        processed: 'bi-check-circle',
        signature_invalid: 'bi-exclamation-triangle',
        failed: 'bi-x-circle',
        ignored: 'bi-slash-circle',
        received: 'bi-inbox'
    }[s] || 'bi-dot');

    return (
        <div className="row g-4">
            <div className="col-12 col-lg-6">
                <SectionCard title="Webhook processing" icon="bi-plug" iconColor={theme.primary}
                    subtitle={`PaymentGatewayWebhookEvent.processing_status — ${total} events in range`} theme={theme}>
                    {!breakdown.length ? <EmptyState message="No webhook events in this range." /> : (
                        <div className="d-flex flex-column gap-3">
                            {breakdown.map((r, i) => (
                                <div key={r.processing_status ?? i}>
                                    <div className="d-flex justify-content-between mb-1 align-items-center">
                                        <span className="small fw-semibold d-flex align-items-center gap-2">
                                            <i className={`bi ${statusIcon(r.processing_status)}`}
                                                style={{ color: statusColor(r.processing_status) }}></i>
                                            {(r.processing_status || 'unknown').replace(/_/g, ' ')}
                                        </span>
                                        <span className="small text-muted">{r.count}</span>
                                    </div>
                                    <div className="progress" style={{ height: 8, borderRadius: 4, background: '#f1f5f9' }}>
                                        <div className="progress-bar"
                                            style={{
                                                width: `${barPct(r.count, total)}%`,
                                                background: statusColor(r.processing_status),
                                                borderRadius: 4
                                            }}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </SectionCard>
            </div>

            <div className="col-12 col-lg-6">
                <SectionCard
                    title="Stuck in progress"
                    icon="bi-hourglass-split"
                    iconColor="#d97706"
                    subtitle={`Older than ${d.stuck_minutes_threshold} min with no webhook — ${d.stuck_payment_count} total`}
                    theme={theme}>
                    {!(d.stuck_payments || []).length ? (
                        <EmptyState message="Nothing stuck. Every in-progress payment has a webhook or is recent." />
                    ) : (
                        <>
                            <div className="table-responsive" style={{ maxHeight: 300, overflowY: 'auto' }}>
                                <table className="table table-sm table-borderless mb-0">
                                    <thead>
                                        <tr style={{ fontSize: '12px', color: theme.primary }}>
                                            <th className="fw-semibold">Order</th>
                                            <th className="fw-semibold text-end">Amount</th>
                                            <th className="fw-semibold text-end">Created</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {d.stuck_payments.map(p => (
                                            <tr key={p.id} style={{ fontSize: '12.5px' }}>
                                                <td><code>{p.gateway_order_id}</code></td>
                                                <td className="text-end fw-semibold">{formatMoney(p.amount, currency)}</td>
                                                <td className="text-end text-muted">{formatDateTime(p.created_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {d.stuck_payments_truncated && (
                                <div className="text-muted mt-2" style={{ fontSize: '11.5px' }}>
                                    <i className="bi bi-info-circle me-1"></i>
                                    Showing the first {d.stuck_payments.length} of {d.stuck_payment_count}.
                                </div>
                            )}
                        </>
                    )}
                </SectionCard>
            </div>
        </div>
    );
}
